import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { ProcessInstance } from './process-instance';
export { ProcessInstance };
import { processInstanceSchema } from '@schemas/business-objects/process-instance';
import { PendingStateUpdateFormArray } from './pending-state-update-form-group';
import { ProcessDesignFormGroup } from './process-design-form-group';
import { ProcessInstanceHistoryFormArray } from './process-instance-history-form-group';
import { TokenFormArray, TokenFormGroup } from './token-form-group';

export class ProcessInstanceFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    referenceNumber: SchemaFormControl;
    name: SchemaFormControl;
    status: SchemaFormControl;
    data: SchemaFormControl;
    errorMessage: SchemaFormControl;
    claimedBy: SchemaFormControl;
    claimedAt: SchemaFormControl;
    completedAt: SchemaFormControl;
    createdAt: SchemaFormControl;
    updatedAt: SchemaFormControl;
    processDesignId: SchemaFormControl;
    processDesign: ProcessDesignFormGroup;
    parentTokenId: SchemaFormControl;
    parentToken: TokenFormGroup;
    processInstanceHistories: ProcessInstanceHistoryFormArray;
    pendingStateUpdates: PendingStateUpdateFormArray;
    tokens: TokenFormArray;
    callingTokens: TokenFormArray;
  };

  declare value: Partial<ProcessInstance>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(processInstanceSchema, ProcessInstance, data, options);
  }

  get object(): ProcessInstance {
    return new ProcessInstance(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'processDesign':
        return new ProcessDesignFormGroup();
      case 'parentToken':
        return new TokenFormGroup();
      case 'processInstanceHistories':
        return new ProcessInstanceHistoryFormArray();
      case 'pendingStateUpdates':
        return new PendingStateUpdateFormArray();
      case 'tokens':
        return new TokenFormArray();
      case 'callingTokens':
        return new TokenFormArray();
      default:
        return undefined;
    }
  }

  async updateState(options: any): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.updateState(options);
  }

  async tryClaim(options: any): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.tryClaim(options);
  }

  async releaseClaim(options: any): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.releaseClaim(options);
  }

  async extendClaim(options: any): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.extendClaim(options);
  }

  async updateData(options: any): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.updateData(options);
  }

  async sendMessage(options: any): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.sendMessage(options);
  }

  async retry(): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.retry();
  }

  async resume(): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.resume();
  }

  async processUpdates(options: any): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.processUpdates(options);
  }

  async pause(): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.pause();
  }

  async fail(options: any): Promise<any> {
    const instance = new ProcessInstance(this.value);
    return instance.fail(options);
  }
}

export class ProcessInstanceFormArray extends PersistedFormArray {
  readonly entityName = 'ProcessInstance' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(processInstanceSchema, ProcessInstance, options);
  }

  protected override createItemGroup() {
    return new ProcessInstanceFormGroup();
  }

  get array(): ProcessInstance[] {
    return this.controls.map((group: ProcessInstanceFormGroup) => group.object);
  }
}

export class ProcessInstancePersistedArray extends PersistedArray<ProcessInstance> {
  readonly entityName = 'ProcessInstance' as const;

  constructor(options?: PersistedArrayOptions) {
    super(ProcessInstance, options);
  }
}
